/**
 * Cálculo do "Período" de uma solicitação de Retenção.
 *
 * O período mede quanto tempo a solicitação já consumiu desde a DATA DE
 * SOLICITAÇÃO (task_retention_details.requested_at) até a conclusão — ou até
 * agora, quando ainda estiver aberta. O resultado é apresentado em faixas
 * (budget) para leitura rápida na listagem e no Kanban.
 *
 * Faixas: 24h | 48h | 72h | 7 dias | 10 dias | 1 mês | 3 meses | 6 meses |
 *         1 ano | Mais de 1 ano
 *
 * IMPORTANTE: estas faixas espelham exatamente a função SQL
 * `public.task_period_bucket`, usada no filtro server-side da listagem e do
 * Kanban (Retenção e Jurídico). Qualquer alteração deve ser feita nos dois.
 *
 * @version 2.0.0
 * RELEASE: 2026-08-22 v2.0.0 - Faixas estendidas e unificadas para Retenção e
 *          Jurídico: 3 meses, 6 meses, 1 ano e "Mais de 1 ano" (antes tudo
 *          acima de 1 mês caía em "Mais de 1 mês")
 * RELEASE: 2026-08-22 v1.0.0 - Criação inicial
 */

/** Identificadores das faixas de período */
export type RetentionPeriodBucket =
  | '24h'
  | '48h'
  | '72h'
  | '7d'
  | '10d'
  | '1m'
  | '3m'
  | '6m'
  | '1y'
  | '1y+';

export interface RetentionPeriod {
  bucket: RetentionPeriodBucket;
  /** Rótulo curto exibido no badge */
  label: string;
  /** Horas decorridas (arredondadas para baixo) */
  hours: number;
  /** Classe utilitária de cor (tokens semânticos do tema) */
  className: string;
  /** Texto completo para tooltip */
  tooltip: string;
}

/** Tabela de faixas — ordem crescente; a primeira que couber vence. */
const BUCKETS: Array<{
  maxHours: number;
  bucket: RetentionPeriodBucket;
  label: string;
  className: string;
}> = [
  { maxHours: 24, bucket: '24h', label: '24h', className: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' },
  { maxHours: 48, bucket: '48h', label: '48h', className: 'bg-sky-500/15 text-sky-600 border-sky-500/30' },
  { maxHours: 72, bucket: '72h', label: '72h', className: 'bg-violet-500/15 text-violet-600 border-violet-500/30' },
  { maxHours: 24 * 7, bucket: '7d', label: '7 dias', className: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
  { maxHours: 24 * 10, bucket: '10d', label: '10 dias', className: 'bg-orange-500/15 text-orange-600 border-orange-500/30' },
  { maxHours: 24 * 30, bucket: '1m', label: '1 mês', className: 'bg-rose-500/15 text-rose-600 border-rose-500/30' },
  // v2.0.0 — faixas longas (necessárias sobretudo no Jurídico)
  { maxHours: 24 * 90, bucket: '3m', label: '3 meses', className: 'bg-rose-500/20 text-rose-700 border-rose-500/40' },
  { maxHours: 24 * 180, bucket: '6m', label: '6 meses', className: 'bg-red-500/20 text-red-700 border-red-500/40' },
  { maxHours: 24 * 365, bucket: '1y', label: '1 ano', className: 'bg-red-600/20 text-red-800 border-red-600/40' },
];

/** Faixa final (acima de 1 ano) */
const OVERFLOW = {
  bucket: '1y+' as const,
  label: 'Mais de 1 ano',
  className: 'bg-destructive/15 text-destructive border-destructive/30',
};

/**
 * Converte "YYYY-MM-DD" (DATE puro do Postgres) em Date local, evitando o
 * deslocamento de fuso que ocorre com `new Date("YYYY-MM-DD")`.
 */
function parseDateOnly(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Calcula o período consumido pela solicitação.
 *
 * @param requestedAt Data da solicitação (DATE ou ISO)
 * @param closedAt    Data de conclusão/cancelamento (opcional). Sem valor, usa agora.
 */
export function calcRetentionPeriod(
  requestedAt: string | null | undefined,
  closedAt?: string | null,
): RetentionPeriod | null {
  if (!requestedAt) return null;
  const start = parseDateOnly(requestedAt);
  if (!start) return null;

  const end = closedAt ? new Date(closedAt) : new Date();
  if (Number.isNaN(end.getTime())) return null;

  const hours = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 3_600_000));
  const match = BUCKETS.find((b) => hours <= b.maxHours);
  const chosen = match ?? OVERFLOW;

  const days = Math.floor(hours / 24);
  const tooltip = closedAt
    ? `Período consumido até a conclusão: ${days > 0 ? `${days} dia(s)` : `${hours}h`}`
    : `Em aberto há ${days > 0 ? `${days} dia(s)` : `${hours}h`} desde a solicitação`;

  return {
    bucket: chosen.bucket,
    label: chosen.label,
    hours,
    className: chosen.className,
    tooltip,
  };
}

/** Opções para filtros/selects de período */
export const RETENTION_PERIOD_OPTIONS: Array<{ value: RetentionPeriodBucket; label: string }> = [
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '72h', label: '72h' },
  { value: '7d', label: '7 dias' },
  { value: '10d', label: '10 dias' },
  { value: '1m', label: '1 mês' },
  { value: '3m', label: '3 meses' },
  { value: '6m', label: '6 meses' },
  { value: '1y', label: '1 ano' },
  { value: '1y+', label: 'Mais de 1 ano' },
];
